import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { TypedMemo } from '../../common/types';
import { useFetchSmartLinkStatsSummariesQuery, useGetSmartLinksQuery } from '../../../redux/api/fymAPI';
import { isSmartLinkDraft } from '../utils/isSmartLinkDraft';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole profile editor; each part uses its own slice of it */
import styles from './SMLProfile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileAddressImpactProps {
  accountId: number;
  savedSlug: string;
  savedPlatformDomain: string;
  slug: string;
  platformDomain: string;
}

/** How many pages we name before the list turns into a count. */
const NAMED_LIMIT = 3;
/** One page of links covers any realistic account; the server caps it at 100. */
const PAGE_LIMIT = 100;

/**
 * What changing the address costs, said before it is changed.
 *
 * The count and the names are the artist's real pages, and the click figures
 * are the same 14-day summaries the SmartLinks home already shows — there is
 * no per-page view total to read, so the panel does not claim one.
 *
 * It says the old address keeps working because it does: an account keeps
 * every name it has ever had (`AccountNameModel` rows are never deleted), the
 * public lookup matches any of them, and the server answers an old address
 * with a redirect to the current one.
 */
const ProfileAddressImpactComponent: React.FC<ProfileAddressImpactProps> = ({
  accountId,
  savedSlug,
  savedPlatformDomain,
  slug,
  platformDomain,
}) => {
  const intl = useIntl();
  const { data: smartLinksResponse } = useGetSmartLinksQuery({ accountId, page: 0, limit: PAGE_LIMIT });
  const { data: summariesResponse } = useFetchSmartLinkStatsSummariesQuery({ accountId });

  const clicksBySmartLink = useMemo(() => {
    const map = new Map<number, number>();
    summariesResponse?.summaries.forEach((summary) => map.set(summary.smartLinkId, summary.clicks));
    return map;
  }, [summariesResponse]);

  const livePages = useMemo(
    () => (smartLinksResponse?.results ?? []).filter((smartLink) => !isSmartLinkDraft(smartLink)),
    [smartLinksResponse]
  );

  const namedPages = useMemo(() => livePages.slice(0, NAMED_LIMIT), [livePages]);

  if (livePages.length === 0) {
    return (
      <div className={styles.notice}>
        <FontAwesomeIcon icon={faTriangleExclamation} />
        <div className={styles.noticeBody}>
          {intl.formatMessage(
            {
              id: 'app.sml.profile.address.impact.none',
              defaultMessage: 'Your address becomes {next}. Nothing is published yet, so no live page changes address.',
            },
            { next: <span key="next" className={styles.noticeMono}>{`${slug}.${platformDomain}`}</span> }
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.notice}>
        <FontAwesomeIcon icon={faTriangleExclamation} />
        <div className={styles.noticeBody}>
          {intl.formatMessage(
            {
              id: 'app.sml.profile.address.impact.summary',
              defaultMessage:
                '{count, plural, one {# live page moves with it.} other {# live pages move with it.}} Every QR code, story link and post using {current} points at the new address instead.',
            },
            {
              count: livePages.length,
              current: <span key="current" className={styles.noticeMono}>{`${savedSlug}.${savedPlatformDomain}`}</span>,
            }
          )}{' '}
          {intl.formatMessage({
            id: 'app.sml.profile.address.impact.redirect',
            defaultMessage: 'The old address keeps working — we send it on to the new one.',
          })}
        </div>
      </div>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>
          {intl.formatMessage({ id: 'app.sml.profile.address.impact.affected', defaultMessage: 'Affected pages' })}
        </span>
        <div className={styles.affectedList}>
          {namedPages.map((smartLink) => {
            const clicks = clicksBySmartLink.get(smartLink.id) ?? 0;
            return (
              <div key={smartLink.id} className={styles.affectedRow}>
                <span className={styles.affectedName}>/{smartLink.recentSmartLinkVersion.slug}</span>
                {clicks > 0 && (
                  <span className={styles.rowNote}>
                    {intl.formatMessage(
                      {
                        id: 'app.sml.profile.address.impact.clicks',
                        defaultMessage: '{clicks, plural, one {# click} other {# clicks}} in 14 days',
                      },
                      { clicks }
                    )}
                  </span>
                )}
                <span className={clsx(styles.pill, styles.pillLive)}>
                  {intl.formatMessage({ id: 'app.sml.smartlinks.status.live', defaultMessage: 'Live' })}
                </span>
              </div>
            );
          })}
          {livePages.length > NAMED_LIMIT && (
            <span className={styles.affectedMore}>
              {intl.formatMessage(
                {
                  id: 'app.sml.profile.address.impact.more',
                  defaultMessage: '+ {count} more',
                },
                { count: livePages.length - NAMED_LIMIT }
              )}
            </span>
          )}
        </div>
      </div>
    </>
  );
};

export const ProfileAddressImpact = TypedMemo(ProfileAddressImpactComponent);
